import apiClient from '@/lib/apiClient';
import { TENANT_HEADER_NAME } from '@/lib/apiTypes';

export type LoginRequest = {
  email: string;
  password: string;
};

export type DummyDataSize = 'Low' | 'Medium' | 'High';

export type DummyAssetCategory =
  | 'BirthdayDecors'
  | 'CampingEquipment'
  | 'Grounds'
  | 'Vehicles'
  | 'MechanicalEquipment';

export type RegisterDummyDataRequest = {
  dataSize: DummyDataSize;
  assetCategory: DummyAssetCategory;
};

export type RegisterRequest = {
  email: string;
  password: string;
  dummyData?: RegisterDummyDataRequest;
};

export type LoginCustomerRequest = {
  name: string;
  phoneNumber: string;
};

export type LoginCustomerResponse = {
  challengeId: string;
};

export type VerifyCustomerOtpRequest = {
  challengeId: string;
  otp: string;
};

export type VerifyCustomerOtpResponse = {
  guid: string;
  email: string;
  firstName: string;
  lastName: string;
};

const REGISTER_TIMEOUT_MS = 120_000;

function getBrowserTimezone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone;
  } catch {
    return 'UTC';
  }
}

function getBrowserLocale(): string {
  if (typeof navigator !== 'undefined' && navigator.language) {
    return navigator.language;
  }
  return 'en-US';
}

function authRequestHeaders() {
  return {
    'Accept-Language': getBrowserLocale(),
    'x-timezone': getBrowserTimezone(),
  };
}

export async function login(request: LoginRequest) {
  const res = await apiClient.post('/auth/login', request, {
    headers: authRequestHeaders(),
  });
  return res.data;
}

export async function loginCustomer(request: LoginCustomerRequest, tenant: string) {
  const res = await apiClient.post<LoginCustomerResponse>('/auth/login/customer', request, {
    headers: {
      ...authRequestHeaders(),
      [TENANT_HEADER_NAME]: tenant,
    },
  });
  return res.data;
}

export async function verifyCustomerOtp(request: VerifyCustomerOtpRequest, tenant: string) {
  const res = await apiClient.post<VerifyCustomerOtpResponse>('/auth/customer:verify-otp', request, {
    headers: {
      ...authRequestHeaders(),
      [TENANT_HEADER_NAME]: tenant,
    },
  });
  return res.data;
}

export async function register(request: RegisterRequest) {
  const res = await apiClient.post<CurrentUserResponse>('/auth/register', request, {
    headers: authRequestHeaders(),
    timeout: REGISTER_TIMEOUT_MS,
  });
  return res.data;
}

export async function refresh() {
  const res = await apiClient.post('/auth/tokens:refresh', {});
  return res.data;
}

export async function logout() {
  const res = await apiClient.post('/auth/logout', {});
  return res.data;
}

export type CurrentUserResponse = {
  guid: string;
  email: string;
  firstName: string;
  lastName: string;
  profilePicture: string;
  createdOn: string;
  lastLoggedOn: string;
  locale: string;
  timezone: string;
  subscriptionDetails: {
    plan: string;
    currentInvoiceCount: number;
    totalAvailableMonthlyInvoiceCount: number;
    currentScheduleCount: number;
    totalAvailableScheduleCount: number;
  };
};

export async function getCurrentUser() {
  const res = await apiClient.get<CurrentUserResponse>('/auth/current-user');
  return res.data;
}

